import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { contractApi } from '../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const Contracts = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const response = await contractApi.getAll();
      setContracts(response.data);
    } catch (error) {
      toast.error('Échec du chargement des contrats');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce contrat ?')) {
      try {
        await contractApi.delete(id);
        toast.success('Contrat supprimé avec succès');
        fetchContracts();
      } catch (error) {
        toast.error('Échec de la suppression du contrat');
        console.error(error);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifié';
    return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Brouillon', class: 'bg-gray-100 text-gray-800', icon: ClockIcon },
      pending_signature: { label: 'En attente de signature', class: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
      signed: { label: 'Signé', class: 'bg-blue-100 text-blue-800', icon: CheckCircleIcon },
      active: { label: 'Actif', class: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      terminated: { label: 'Résilié', class: 'bg-red-100 text-red-800', icon: DocumentTextIcon }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        <Icon className="h-4 w-4 mr-1" />
        {config.label}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contrats de Location</h1>
          <p className="text-gray-600 mt-1">Contrats de location de logement nu (non meublé) - France uniquement</p>
        </div>
        <Link
          to="/contracts/new"
          className="btn-primary inline-flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-1" />
          Nouveau Contrat
        </Link>
      </div>

      {loading ? (
        <div className="text-center py-8">Chargement des contrats...</div>
      ) : contracts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <DocumentTextIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun contrat trouvé</h3>
          <p className="text-gray-600 mb-4">
            Commencez par créer votre premier contrat de location.
          </p>
          <Link
            to="/contracts/new"
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-1" />
            Créer un Contrat
          </Link>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {contracts.map((contract) => (
              <li key={contract.id}>
                <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-lg font-medium text-primary-600 truncate">
                          {contract.tenantFullName}
                        </p>
                        {getStatusBadge(contract.status)}
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between">
                        <div className="sm:flex">
                          <p className="flex items-center text-sm text-gray-500">
                            <DocumentTextIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                            {contract.property?.name || contract.propertyAddress}
                          </p>
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                          <p>
                            {formatDate(contract.contractStartDate)}
                            {contract.contractDuration && ` - ${contract.contractDuration} ans`}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <p>Loyer: {contract.monthlyRent}€/mois</p>
                        {contract.signatureDate && (
                          <p className="ml-4">
                            Signé le: {formatDate(contract.signatureDate)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0 flex space-x-2">
                      <Link
                        to={`/contracts/${contract.id}`}
                        className="text-primary-600 hover:text-primary-900 p-2"
                        title="Voir"
                      >
                        <DocumentTextIcon className="h-5 w-5" />
                      </Link>
                      {contract.status === 'draft' && (
                        <Link
                          to={`/contracts/edit/${contract.id}`}
                          className="text-blue-600 hover:text-blue-800 p-2"
                          title="Modifier"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </Link>
                      )}
                      {contract.status !== 'active' && (
                        <button
                          onClick={() => handleDelete(contract.id)}
                          className="text-red-600 hover:text-red-800 p-2"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Contracts;
